/* eslint-disable no-shadow */
import Vuetify from "@/plugins/vuetify";
import FetchStatus from "@/constants/fetch-status/FetchStatus";
import { httpV2 } from "@/api/index";
import { ChartLinesColorsDark, SetLayoutLinesColor } from "@/components/charts/ChartStyles";
import {
	GET_GEARBOX_PLANETARY_CHART,
	GET_GEARBOX_PLANETARY_CHART_SUCCESS,
	GET_GEARBOX_PLANETARY_CHART_ERROR,
	SHOW_CONCERN_FREQUENCIES,
	HIDE_CONCERN_FREQUENCIES,
} from "@/stores/actions/GearboxPlanetaryDiagnostic";
import WDService from "@/services/wind/diagnostic/WindDiagnosticService";

import GearComponents from "@/constants/diagnostic/GearComponents";

import SortingService from "@/services/common/SortingService";

const state = {
	frequencyEnvelopeChartData: [],
	orderEnvelopeChartData:     [],
	frequencyEnvelopeLayout:    {},
	orderEnvelopeLayout:        {},

	frequencySpectrumChartData: [],
	orderSpectrumChartData:     [],
	frequencySpectrumLayouts:   {},
	orderSpectrumLayouts:       {},

	concernFrequencies: [],
	concernOrders:      [],

	status: FetchStatus.Init,
	error:  {},
};

/**
* @summary Cria as anotações e atribui na váriavel layout.
* @param {Array} orders Array com as posiçoes do eixo x das anotações.
*/
const CreateAnnotations = (orders) =>
{
	const shapes = [];
	for (let i = 0; i < orders.length; i++)
	{
		const shape = {
			yref: "paper",
			type: "line",
			x0:   orders[i],
			x1:   orders[i],
			y0:   0, // Representa 0%
			y1:   1, // Representa 100%
			line: {
				color: ChartLinesColorsDark[0],
				width: 2,
				dash:  "dot",
			},
			visible: false,
		};
		shapes.push(shape);
	}
	return shapes;
};

/**
* @summary Cria a anotação específica para ordem de engrenamento.
* @param {Array} gearingOrder Ordem de engrenamento.
*/
const CreateGearingOrderAnnotation = (gearingOrder) =>
{
	const shape = {
		yref: "paper",
		type: "line",
		x0:   gearingOrder,
		x1:   gearingOrder,
		y0:   0, // Representa 0%
		y1:   1, // Representa 100%
		line: {
			color: ChartLinesColorsDark[2],
			width: 2,
			dash:  "dot",
		},
		visible: true,
	};
	return shape;
};

/**
* @summary Cria os textos para as anotações
* @param {Array - Numbers} orders - Posições das anotações no eixo Y.
* @param {String} componentName - Nome do componente.
* @param {String} darkMode - booleano que indica se o sistema está com o dark mode ativado.
*/
const CreateAnnotationText = (orders, componentName, darkMode, gmf) =>
{
	const textColor = darkMode ? Vuetify.framework.theme.themes.dark.secondary :
		Vuetify.framework.theme.themes.light.secondary;

	const annotations = [];
	let positiveValuesCount = 0;
	let negativeValuesCount = orders.length / 2;

	for (let i = 0; i < orders.length; i++)
	{
		let text = "";
		if (orders[i] > gmf)
		{
			text = `${positiveValuesCount + 1}x ${componentName}`;
			positiveValuesCount += 1;
		}
		else
		{
			text = `${negativeValuesCount}x ${componentName}`;
			negativeValuesCount -= 1;
		}

		const annotation = {
			x:          orders[i],
			y:          0.8, // 80 % do valor em Y
			yref:       "paper",
			mode:       "text",
			showlegend: false,
			text,
			showarrow:  false,
			visible:    false,
			textangle:  270,
			align:      "right",
			yanchor:    "top",
			xshift:     10,
			yshift:     30,
			font:       { color: textColor },
		};
		annotations.push(annotation);
	}
	return annotations;
};

/**
* @summary Cria os textos para a anotação da ordem de engrenamento.
* @param {Array - Numbers} gearingOrder - Ordem de engrenamento.
* @param {String} darkMode - booleano que indica se o sistema está com o dark mode ativado.
*/
const CreateGearingOrderAnnotationText = (gearingOrder, darkMode) =>
{
	const textColor = darkMode ? Vuetify.framework.theme.themes.dark.secondary :
		Vuetify.framework.theme.themes.light.secondary;

	const annotation = {
		x:          gearingOrder,
		y:          0.8, // 80 % do valor em Y
		yref:       "paper",
		mode:       "text",
		showlegend: false,
		text:       "GMF",
		showarrow:  false,
		visible:    true,
		textangle:  270,
		align:      "right",
		yanchor:    "top",
		xshift:     10,
		yshift:     30,
		font:       { color: textColor },
	};
	return annotation;
};

const concernMappings = {
	[GearComponents.PLANET]: {
		concernOrders:      "planetConcernOrders",
		concernFrequencies: "planetConcernFrequencies"
	},
	[GearComponents.SUN]: {
		concernOrders:      "sunConcernOrders",
		concernFrequencies: "sunConcernFrequencies"
	},
	[GearComponents.RING]: {
		concernOrders:      "cageConcernOrders",
		concernFrequencies: "cageConcernFrequencies"
	}
};

const actions = {
	[GET_GEARBOX_PLANETARY_CHART]: ({ commit }, parameters) =>
	{
		commit(GET_GEARBOX_PLANETARY_CHART);
		httpV2.get(`/WindTurbinesDiagnostic/${parameters.resultSetId}/Planetary/Chart`)
			.then((resp) =>
			{
				for (let i = 0; i < resp.data.fullSpectrumCharts.length; i++)
				{
					const charts = resp.data.fullSpectrumCharts[i].charts;
					SortingService.SortListAlphabeticallyByName(charts);
				}
				SortingService.SortListAlphabeticallyByName(resp.data.envelopeCharts);

				const spectrumCharts = resp.data.fullSpectrumCharts;
				const envelopeCharts = resp.data.envelopeCharts;

				const concernOrdersEnvelope = envelopeCharts[0][concernMappings[parameters.component].concernOrders];
				const concernFrequencyEnvelope = envelopeCharts[0][concernMappings[parameters.component].concernFrequencies];

				// Objeto que contém a estrutura dos dados necessários para a plotagem do gráfico.
				const response = {
					orderChartData:          { envelopeChart: [], fullSpectrumCharts: [] },
					frequencyChartData:      { envelopeChart: [], fullSpectrumCharts: [] },
					spectrumOrderLayout:     [],
					spectrumFrequencyLayout: [],
					envelopeOrderLayout:     {},
					envelopeFrequencyLayout: {},
					regionFrequency:         0,
					regionOrder:             0,
				};
				
				// Realiza a criação da estrutura utilizada pelo gráfico, apenas do espectro de envelope.
				response.orderChartData.envelopeChart = WDService.CreateChart(envelopeCharts, true);
				response.frequencyChartData.envelopeChart = WDService.CreateChart(envelopeCharts, false);

				// Itera sobre os dados dos gráficos do espectro bruto.
				for (let i = 0; i < spectrumCharts.length; i++)
				{
					// Simplifica a menção do valor da variável.
					const charts = spectrumCharts[i].charts;

					// Realiza a criação da estrutura utilizada pelo gráfico, apenas do espectro bruto.
					const orderChart = WDService.CreateChart(charts, true);
					const frequencyChart = WDService.CreateChart(charts, false);

					// Insere na lista, cada estrutura montada para o gráfico.
					response.orderChartData.fullSpectrumCharts.push(orderChart);
					response.frequencyChartData.fullSpectrumCharts.push(frequencyChart);
				}

				// Atribui valor recebido de resposta para a estrutura base.
				response.regionFrequency = resp.data.gearMeshFrequency;
				response.regionOrder = resp.data.gearMeshOrder;

				response.envelopeFrequencyLayout = SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.isDarkMode);

				response.envelopeOrderLayout = SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.isDarkMode);

				response.envelopeFrequencyLayout.shapes = WDService.CreateShapes(concernFrequencyEnvelope);
				response.envelopeFrequencyLayout.annotations =
					WDService.CreateAnnotationText(concernFrequencyEnvelope, parameters.isDarkMode, parameters.component);

				response.envelopeOrderLayout.shapes =  WDService.CreateShapes(concernOrdersEnvelope);
				response.envelopeOrderLayout.annotations =
					WDService.CreateAnnotationText(concernOrdersEnvelope, parameters.isDarkMode, parameters.component);

				for (let i = 0; i < response.orderChartData.fullSpectrumCharts.length; i++)
				{
					const charts = spectrumCharts[i].charts;

					const concernFrequencies = charts[0][concernMappings[parameters.component].concernFrequencies];
					const concernOrders = charts[0][concernMappings[parameters.component].concernOrders];

					const orderLayout = SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.isDarkMode);
					const frequencyLayout = SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.isDarkMode);
					
					const gmfHarmonic = response.regionFrequency * (i + 1);
					const gmoHarmonic = response.regionOrder * (i + 1);

					const ordersShapes = CreateAnnotations(concernOrders);
					
					const ordersAnnotations =
						CreateAnnotationText(concernOrders, parameters.component, parameters.isDarkMode, gmoHarmonic);

					const frequenciesShapes = CreateAnnotations(concernFrequencies);
					const frequenciesAnnotations =
						CreateAnnotationText(concernFrequencies, parameters.component, parameters.isDarkMode, gmfHarmonic);

					ordersShapes.push(CreateGearingOrderAnnotation(gmoHarmonic));
					ordersAnnotations.push(CreateGearingOrderAnnotationText(gmoHarmonic, parameters.isDarkMode));

					frequenciesShapes.push(CreateGearingOrderAnnotation(gmfHarmonic));
					frequenciesAnnotations.push(CreateGearingOrderAnnotationText(gmfHarmonic, parameters.isDarkMode));
					
					orderLayout.shapes = ordersShapes;
					orderLayout.annotations = ordersAnnotations;

					orderLayout.legend.font.size = 14;
					orderLayout.legend.y = 2;

					frequencyLayout.shapes = frequenciesShapes;
					frequencyLayout.annotations = frequenciesAnnotations;

					orderLayout.legend.font.size = 14;
					orderLayout.legend.y = 2;

					response.spectrumOrderLayout.push(orderLayout);
					response.spectrumFrequencyLayout.push(frequencyLayout);
				}

				commit(GET_GEARBOX_PLANETARY_CHART_SUCCESS, response);
			})
			.catch((error) =>
			{
				commit(GET_GEARBOX_PLANETARY_CHART_ERROR, error.response);
			});
	},

	[SHOW_CONCERN_FREQUENCIES]: ({ commit }) =>
	{
		commit(SHOW_CONCERN_FREQUENCIES);
	},

	[HIDE_CONCERN_FREQUENCIES]: ({ commit }) =>
	{
		commit(HIDE_CONCERN_FREQUENCIES);
	},
};

const mutations = {
	[GET_GEARBOX_PLANETARY_CHART]: (state) =>
	{
		state.status = FetchStatus.Loading;
	},
	[GET_GEARBOX_PLANETARY_CHART_SUCCESS]: (state, resp) =>
	{
		state.frequencyEnvelopeChartData = resp.frequencyChartData.envelopeChart;
		state.orderEnvelopeChartData = resp.orderChartData.envelopeChart;
		state.frequencyEnvelopeLayout = resp.envelopeFrequencyLayout;
		state.orderEnvelopeLayout = resp.envelopeOrderLayout;

		state.frequencySpectrumChartData = resp.frequencyChartData.fullSpectrumCharts;
		state.orderSpectrumChartData = resp.orderChartData.fullSpectrumCharts;
		state.frequencySpectrumLayouts = resp.spectrumFrequencyLayout;
		state.orderSpectrumLayouts = resp.spectrumOrderLayout;
		
		state.status = FetchStatus.Loaded;
	},
	[SHOW_CONCERN_FREQUENCIES]: (state) =>
	{
		state.showConcernFrequencies = true;
		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(state.component))
			{
				state.orderLayout.shapes[i].visible = true;
				state.orderLayout.annotations[i].visible = true;

				state.frequencyLayout.shapes[i].visible = true;
				state.frequencyLayout.annotations[i].visible = true;
			}
			state.orderLayout.yaxis.showgrid = false;
			state.orderLayout.xaxis.showgrid = false;

			state.frequencyLayout.yaxis.showgrid = false;
			state.frequencyLayout.xaxis.showgrid = false;
		}
	},

	[HIDE_CONCERN_FREQUENCIES]: (state) =>
	{
		state.showConcernFrequencies = false;
		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(state.component))
			{
				state.orderLayout.shapes[i].visible = false;
				state.orderLayout.annotations[i].visible = false;
				state.orderLayout.yaxis.showgrid = true;
				state.orderLayout.xaxis.showgrid = true;

				state.frequencyLayout.shapes[i].visible = false;
				state.frequencyLayout.annotations[i].visible = false;
				state.frequencyLayout.yaxis.showgrid = true;
				state.frequencyLayout.xaxis.showgrid = true;
			}
		}
	},
};

export default {
	state,
	actions,
	mutations,
};
